import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { getLoginUsername } from "./PermissionsReducer";
import { createAuditLog } from "../utils/commonServices";

const initialState = {
	maintenance: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_MAINTENANCE_REQUEST = "FETCH_MAINTENANCE_REQUEST";
const FETCH_MAINTENANCE_SUCCESS = "FETCH_MAINTENANCE_SUCCESS";
const FETCH_MAINTENANCE_FAILURE = "FETCH_MAINTENANCE_FAILURE";
const CREATE_MAINTENANCE_REQUEST = "CREATE_MAINTENANCE_REQUEST";
const CREATE_MAINTENANCE_SUCCESS = "CREATE_MAINTENANCE_SUCCESS";
const CREATE_MAINTENANCE_FAILURE = "CREATE_MAINTENANCE_FAILURE";
const UPDATE_MAINTENANCE_REQUEST = "UPDATE_MAINTENANCE_REQUEST";
const UPDATE_MAINTENANCE_SUCCESS = "UPDATE_MAINTENANCE_SUCCESS";
const UPDATE_MAINTENANCE_FAILURE = "UPDATE_MAINTENANCE_FAILURE";
const DELETE_MAINTENANCE_REQUEST = "DELETE_MAINTENANCE_REQUEST";
const DELETE_MAINTENANCE_SUCCESS = "DELETE_MAINTENANCE_SUCCESS";
const DELETE_MAINTENANCE_FAILURE = "DELETE_MAINTENANCE_FAILURE";

// Fields to track for audit logging
const AUDIT_FIELDS = [
	"equipmentId",
	"date",
	"type",
	"description",
	"status",
	"cost",
	"performedBy",
	"notes",
	"fundingSourceId",
	"perPersonnelId",
	"nextDueDate",
	"odometer",
	"location",
	"serviceProvider",
	"warrantyInfo",
];

// Single Audit Log Helper for Maintenance
const logMaintenanceAudit = async ({
	action,
	recordId,
	oldData = null,
	newData = null,
	agencyId,
	additionalInfo = null,
}) => {
	try {
		let oldValues = null;
		let newValues = null;

		if (action === "CREATE") {
			// For CREATE, log all new values
			newValues = {};
			AUDIT_FIELDS.forEach((field) => {
				if (
					newData?.[field] !== undefined &&
					newData?.[field] !== null
				) {
					newValues[field] = newData[field];
				}
			});
			// Add additional info if provided
			if (additionalInfo) {
				newValues = { ...newValues, ...additionalInfo };
			}
		} else if (action === "DELETE") {
			// For DELETE, log key old values
			oldValues = {};
			AUDIT_FIELDS.forEach((field) => {
				if (
					oldData?.[field] !== undefined &&
					oldData?.[field] !== null
				) {
					oldValues[field] = oldData[field];
				}
			});
		} else if (action === "UPDATE") {
			// For UPDATE, log only changed fields
			oldValues = {};
			newValues = {};
			AUDIT_FIELDS.forEach((field) => {
				const oldVal = oldData?.[field];
				const newVal = newData?.[field];
				if (oldVal !== newVal) {
					oldValues[field] = oldVal ?? null;
					newValues[field] = newVal ?? null;
				}
			});

			// Add additional info if provided
			if (additionalInfo) {
				newValues = { ...newValues, ...additionalInfo };
			}

			// Don't log if no changes
			if (
				Object.keys(oldValues).length === 0 &&
				Object.keys(newValues).length === 0
			) {
				return;
			}
		}

		await createAuditLog({
			tableName: "EquipmentMaintenance",
			recordId,
			action,
			changedBy: getLoginUsername() || "",
			changedAt: new Date().toISOString(),
			oldValues: oldValues ? JSON.stringify(oldValues) : null,
			newValues: newValues ? JSON.stringify(newValues) : null,
			agencyId,
		});
	} catch (error) {
		console.error("Failed to create maintenance audit log:", error);
		// Don't throw - audit failure shouldn't break main operation
	}
};

export const getMaintenanceList =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "desc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		if (search) {
			query.$or = [
				{ description: { $like: `%${search}%` } },
				{ notes: { $like: `%${search}%` } },
				{ status: { $like: `%${search}%` } },
				{ performedBy: { $like: `%${search}%` } },
			];
		}

		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		const multiSelectFields = [
			"equipmentId",
			"perPersonnelId",
			"fundingSourceId",
			"performedBy",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		// Status filter
		if (filters.status?.value && filters.status.value !== "all") {
			query.status = filters.status.value;
		}

		// Date range filters
		if (filters.from) {
			query.date = { ...(query.date || {}), $gte: filters.from };
		}
		if (filters.to) {
			query.date = { ...(query.date || {}), $lte: filters.to };
		}

		// Period filter
		if (filters.period?.value) {
			const now = new Date();
			let startDate;

			switch (filters.period.value) {
				case "today":
					startDate = new Date(now.setHours(0, 0, 0, 0));
					break;
				case "week":
					startDate = new Date(now.setDate(now.getDate() - 7));
					break;
				case "month":
					startDate = new Date(now.setMonth(now.getMonth() - 1));
					break;
				case "quarter":
					startDate = new Date(now.setMonth(now.getMonth() - 3));
					break;
				case "year":
					startDate = new Date(
						now.setFullYear(now.getFullYear() - 1),
					);
					break;
				default:
					break;
			}

			if (startDate) {
				query.date = {
					...(query.date || {}),
					$gte: startDate.toISOString(),
				};
			}
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_MAINTENANCE_REQUEST,
				success: FETCH_MAINTENANCE_SUCCESS,
				failure: FETCH_MAINTENANCE_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });
				return {
					maintenance: response?.data || [],
					total: response.total || 0,
				};
			},
			transformResult: (res) => ({
				maintenance: res?.maintenance,
				total: res?.total,
			}),
			errorMessagePrefix: "Failed to fetch maintenance records",
		});
	};

export const createMaintenance =
	(data, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_MAINTENANCE_REQUEST,
				success: CREATE_MAINTENANCE_SUCCESS,
				failure: CREATE_MAINTENANCE_FAILURE,
			},
			apiCall: async () => {
				const record = await service.create({
					...data,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Create Audit Log Entry
				await logMaintenanceAudit({
					action: "CREATE",
					recordId: record.id,
					newData: data,
					agencyId: userAgency,
				});

				return record;
			},
			successMessage: "Maintenance record created successfully",
			errorMessagePrefix: "Failed to create maintenance record",
		});
	};

export const updateMaintenance =
	(id, data, userId, userAgency, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_MAINTENANCE_REQUEST,
				success: UPDATE_MAINTENANCE_SUCCESS,
				failure: UPDATE_MAINTENANCE_FAILURE,
			},
			apiCall: async () => {
				const record = await service.patch(id, {
					...data,
					updatedBy: userId,
				});

				// Create Audit Log Entry
				await logMaintenanceAudit({
					action: "UPDATE",
					recordId: id,
					oldData,
					newData: data,
					agencyId: userAgency,
				});

				return record;
			},
			successMessage: "Maintenance record updated successfully",
			errorMessagePrefix: "Failed to update maintenance record",
		});
	};

export const deleteMaintenance =
	(id, userId, userAgency, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: DELETE_MAINTENANCE_REQUEST,
				success: DELETE_MAINTENANCE_SUCCESS,
				failure: DELETE_MAINTENANCE_FAILURE,
			},
			apiCall: async () => {
				await service.patch(id, {
					isDeleted: true,
					deletedBy: userId,
					deletedDateTime: new Date().toISOString(),
				});

				// Create Audit Log Entry
				await logMaintenanceAudit({
					action: "DELETE",
					recordId: id,
					oldData,
					agencyId: userAgency,
				});

				return id;
			},
			transformResult: () => id,
			successMessage: "Maintenance record deleted successfully",
			errorMessagePrefix: "Failed to delete maintenance record",
		});
	};

export default function maintenanceReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_MAINTENANCE_REQUEST:
		case CREATE_MAINTENANCE_REQUEST:
		case UPDATE_MAINTENANCE_REQUEST:
		case DELETE_MAINTENANCE_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_MAINTENANCE_SUCCESS:
			return {
				...state,
				loading: false,
				maintenance: action.payload.maintenance,
				total: action.payload.total,
			};
		case CREATE_MAINTENANCE_SUCCESS:
			return {
				...state,
				loading: false,
				maintenance: [action.payload, ...state.maintenance],
				total: state.total + 1,
			};
		case UPDATE_MAINTENANCE_SUCCESS:
			return {
				...state,
				loading: false,
				maintenance: state.maintenance.map((m) =>
					m.id === action.payload.id ? action.payload : m,
				),
			};
		case DELETE_MAINTENANCE_SUCCESS:
			return {
				...state,
				loading: false,
				maintenance: state.maintenance.filter(
					(m) => m.id !== action.payload,
				),
				total: state.total - 1,
			};
		case FETCH_MAINTENANCE_FAILURE:
		case CREATE_MAINTENANCE_FAILURE:
		case UPDATE_MAINTENANCE_FAILURE:
		case DELETE_MAINTENANCE_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
